import { useState } from "react";
import { useCustomMutation, useTranslate } from "@refinedev/core";
import { Button, Field, FormCard, PasswordField, SectionHeader, StatusBadge } from "@fawkes/ui";

import type { RefreshOutcome, RotationOutcome } from "./types";

// jmix-9.3 + runbook Mode A: master-key rotation is a TWO-step operator flow.
//   1. "Rotate now" → POST /api/admin/organizations/rotate-master-key re-encrypts every organization's outbound
//      secret under a new master key+salt (transactional, row writes). The running encryptor still
//      holds the OLD keys afterwards.
//   2. After the operator updates the deployment config, "Reload encryption keys from env" →
//      POST /api/admin/organizations/reload-encryption-keys swaps the live encryptor delegate IN PLACE
//      (no row writes, no restart) by re-resolving the keys from the environment.
// A confirm-phrase guard (per mode) mirrors the legacy view for instant feedback; the real authority
// is M7 + server-side validation. The reload mode hides the key/salt fields (the keys come from the
// env, never the wire) and renders the four RefreshOutcome banners per the runbook copy.

const ROTATE_CONFIRM = "rotate-organization-keys";
const RELOAD_CONFIRM = "reload-encryption-keys";

type Mode = "rotate" | "reload";

export function OrganizationRotation() {
  const t = useTranslate();
  const { mutate, isLoading: mutating } = useCustomMutation();

  const [mode, setMode] = useState<Mode>("rotate");
  const [newKey, setNewKey] = useState("");
  const [newSalt, setNewSalt] = useState("");
  const [confirm, setConfirm] = useState("");
  const [outcome, setOutcome] = useState<RotationOutcome | null>(null);
  const [refresh, setRefresh] = useState<RefreshOutcome | null>(null);
  const [error, setError] = useState<string | undefined>();

  const confirmPhrase = mode === "rotate" ? ROTATE_CONFIRM : RELOAD_CONFIRM;
  // Reload mode reads its keys from the env, so it needs no key/salt input — only the confirm phrase.
  const keysReady = mode === "reload" || (newKey.trim().length > 0 && newSalt.trim().length > 0);
  const ready = keysReady && confirm.trim() === confirmPhrase;

  function switchMode(next: Mode) {
    if (next === mode) {
      return;
    }
    // Reset transient state so a half-typed confirm / a stale outcome from the other mode can't leak.
    setMode(next);
    setConfirm("");
    setOutcome(null);
    setRefresh(null);
    setError(undefined);
  }

  function submit() {
    setError(undefined);
    setOutcome(null);
    setRefresh(null);
    if (!ready) {
      return;
    }
    if (mode === "rotate") {
      mutate(
        {
          url: "admin/organizations/rotate-master-key",
          method: "post",
          values: { newKey: newKey.trim(), newSalt: newSalt.trim() },
        },
        {
          onSuccess: (res) => {
            setOutcome((res?.data as RotationOutcome) ?? null);
            // Clear the new master key + salt once the rotation has landed — the server holds them
            // now; keeping the plaintext around only risks an accidental re-submit.
            setNewKey("");
            setNewSalt("");
            setConfirm("");
          },
          onError: () => setError(t("admin.rotation.error")),
        },
      );
    } else {
      mutate(
        // No body: the keys come from the env, never the wire.
        { url: "admin/organizations/reload-encryption-keys", method: "post", values: {} },
        {
          onSuccess: (res) => {
            setRefresh((res?.data as RefreshOutcome) ?? null);
            setConfirm("");
          },
          onError: () => setError(t("admin.reload.error")),
        },
      );
    }
  }

  return (
    <div className="fawkes-portal">
      <FormCard>
        <SectionHeader>{t("admin.rotation.title")}</SectionHeader>
        <p className="fawkes-portal__muted">
          {mode === "rotate" ? t("admin.rotation.intro") : t("admin.reload.intro")}
        </p>

        <fieldset className="fawkes-admin__modes">
          <legend className="fawkes-portal__muted">{t("admin.rotation.mode.label")}</legend>
          <label>
            <input
              type="radio"
              name="rotation-mode"
              checked={mode === "rotate"}
              onChange={() => switchMode("rotate")}
            />
            {t("admin.rotation.mode.rotate")}
          </label>
          <label>
            <input
              type="radio"
              name="rotation-mode"
              checked={mode === "reload"}
              onChange={() => switchMode("reload")}
            />
            {t("admin.rotation.mode.reload")}
          </label>
        </fieldset>

        {mode === "rotate" ? (
          <>
            <PasswordField
              label={t("admin.rotation.newKey")}
              revealLabel={t("auth.field.revealPassword")}
              hideLabel={t("auth.field.hidePassword")}
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
            />
            <p className="fawkes-portal__muted">{t("admin.rotation.newKey.help")}</p>
            <Field
              label={t("admin.rotation.newSalt")}
              value={newSalt}
              onChange={(e) => setNewSalt(e.target.value)}
            />
            <p className="fawkes-portal__muted">{t("admin.rotation.newSalt.help")}</p>
          </>
        ) : null}

        <Field
          label={mode === "rotate" ? t("admin.rotation.confirm") : t("admin.reload.confirm")}
          placeholder={confirmPhrase}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />

        <div className="fawkes-portal__toolbar">
          <Button disabled={!ready || mutating} onClick={submit}>
            {mode === "rotate"
              ? mutating
                ? t("admin.rotation.rotating")
                : t("admin.rotation.submit")
              : mutating
                ? t("admin.reload.reloading")
                : t("admin.reload.submit")}
          </Button>
        </div>

        {outcome ? (
          <div className="fawkes-admin__outcome" role="status">
            <StatusBadge tone={outcome.outcome === "failed" ? "danger" : "success"}>
              {t(`admin.rotation.outcome.${outcome.outcome === "failed" ? "failed" : outcome.outcome === "already_rotated" ? "alreadyRotated" : "success"}`)}
            </StatusBadge>
            {outcome.outcome === "success" ? (
              <>
                {/* Post-rotation health summary: the affected rows were re-encrypted under the new keys
                    inside the transaction (the rotation verifies each row decrypts before committing). */}
                <p className="fawkes-portal__muted">
                  {outcome.affected ?? 0} {t("admin.rotation.success.lead")}
                </p>
                <p className="fawkes-portal__muted">
                  {outcome.affected ?? 0} {t("admin.rotation.verified")}
                  {outcome.keyHashPrefix ? ` · ${outcome.keyHashPrefix}…` : ""}
                </p>
              </>
            ) : null}
            {outcome.outcome === "already_rotated" ? (
              <p className="fawkes-portal__muted">{t("admin.rotation.alreadyRotated.detail")}</p>
            ) : null}
            {outcome.outcome === "failed" ? (
              <p className="fawkes-portal__error">
                {t("admin.rotation.failed.detail")}
                {outcome.offendingOrganization || outcome.reason
                  ? ` (${outcome.offendingOrganization ?? "—"}: ${outcome.reason ?? "—"})`
                  : ""}
              </p>
            ) : null}
          </div>
        ) : null}

        {refresh ? (
          <div className="fawkes-admin__outcome" role="status">
            <StatusBadge
              tone={
                refresh.outcome === "success"
                  ? "success"
                  : refresh.outcome === "no_change"
                    ? "neutral"
                    : "danger"
              }
            >
              {t(`admin.reload.outcome.${refresh.outcome}`)}
            </StatusBadge>
            {refresh.outcome === "success" ? (
              <p className="fawkes-portal__muted">
                {t("admin.reload.success.lead")} {refresh.evictedCacheEntries ?? 0}{" "}
                {t("admin.reload.success.evicted")}
              </p>
            ) : null}
            {refresh.outcome === "no_change" ? (
              <p className="fawkes-portal__muted">{t("admin.reload.noChange.detail")}</p>
            ) : null}
            {refresh.outcome === "failed" ? (
              <p className="fawkes-portal__error">
                {t("admin.reload.failed.detail")}
                {refresh.reason ? ` (${refresh.reason})` : ""}
              </p>
            ) : null}
            {refresh.outcome === "disabled" ? (
              <p className="fawkes-portal__error">{t("admin.reload.disabled.detail")}</p>
            ) : null}
          </div>
        ) : null}

        {error ? (
          <p className="fawkes-portal__error" role="alert">
            {error}
          </p>
        ) : null}
      </FormCard>
    </div>
  );
}
